import { useMutation, useQuery } from '@apollo/client'
import { Autocomplete, Box, Tab, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    GetCompanyClientContacts,
    UpdateCompanyClientContacts,
} from 'recruiter/graphql/companies.gql'
import { ContactGroups } from 'recruiter/types/ContactGroups'
import useToast from 'shared/components/Toast/useToast'
import { getPxAsRem } from 'shared/utils'
import { formatGraphQLError } from 'shared/utils/graphQL'
import { ContentWrapper, StyledTabs } from './styled'

import type { CompanyClientContactEntry } from '__generated__/graphql'
import type { FC } from 'react'
import DrawerComponent from 'shared/components/DrawerComponent'

interface ClientContactsDrawerProps {
    open: boolean
    onClose: () => void
}

const ClientContactsDrawer: FC<ClientContactsDrawerProps> = ({
    open,
    onClose,
}) => {
    const toast = useToast()
    const companyId = useParams().companyId ?? ''
    const { data } = useQuery(GetCompanyClientContacts, {
        variables: { companyId },
    })

    const [updateClientContactsMutation] = useMutation(
        UpdateCompanyClientContacts
    )
    const [selectedTab, setSelectedTab] =
        useState<keyof typeof ContactGroups>('Pre-MBA')
    const [primaryContact, setPrimaryContact] =
        useState<CompanyClientContactEntry | null>(null)
    const [secondaryContact, setSecondaryContact] =
        useState<CompanyClientContactEntry | null>(null)

    useEffect(() => {
        const primary =
            data?.company.clientContacts?.find(
                (contact) =>
                    contact.group === selectedTab &&
                    contact.contactType === 'Primary Contact'
            ) || null
        const secondary =
            data?.company.clientContacts?.find(
                (contact) =>
                    contact.group === selectedTab &&
                    contact.contactType === 'Secondary Contact'
            ) || null

        setPrimaryContact(primary)
        setSecondaryContact(secondary)
    }, [selectedTab, data])

    const handleTabChange = (_, newValue) => {
        setSelectedTab(newValue)
    }

    const [updatedContacts, setUpdatedContacts] = useState<
        Map<string, CompanyClientContactEntry>
    >(new Map())

    useEffect(() => {
        if (data?.company.clientContacts) {
            const initialContacts = new Map<string, CompanyClientContactEntry>()
            data.company.clientContacts.forEach((contact) => {
                const key = `${contact.group}-${contact.contactType}`
                initialContacts.set(key, {
                    candidateId: contact.candidateId ?? '',
                    group: contact.group,
                    contactType: contact.contactType,
                })
            })
            setUpdatedContacts(initialContacts)
        }
    }, [data])

    const getUpdatedClientContacts = () => {
        return Array.from(updatedContacts.values())
    }
    const handlePrimaryContactChange = (newValue) => {
        const key = `${selectedTab}-Primary Contact`

        if (newValue) {
            updatedContacts.set(key, {
                candidateId: newValue.id,
                group: selectedTab,
                contactType: 'Primary Contact',
            })
        } else {
            updatedContacts.delete(key)
        }

        setUpdatedContacts(new Map(updatedContacts))
    }

    const handleSecondaryContactChange = (newValue) => {
        const key = `${selectedTab}-Secondary Contact`

        if (newValue) {
            updatedContacts.set(key, {
                candidateId: newValue.id,
                group: selectedTab,
                contactType: 'Secondary Contact',
            })
        } else {
            updatedContacts.delete(key)
        }

        setUpdatedContacts(new Map(updatedContacts))
    }

    const handleSave = () => {
        updateClientContactsMutation({
            variables: {
                companyId,
                clientContacts: getUpdatedClientContacts(),
            },
            onCompleted: (data) => {
                if (data.updateCompanyClientContacts.success) {
                    toast({
                        title: 'Success',
                        severity: 'success',
                        message: 'Client contacts updated successfully',
                    })
                    onClose()
                } else {
                    toast({
                        title: 'Error',
                        severity: 'error',
                        message:
                            data.updateCompanyClientContacts.message ??
                            'Failed to update client contacts. Please try again.',
                    })
                }
            },
            onError: (error) => {
                toast({
                    title: 'Error',
                    severity: 'error',
                    message: formatGraphQLError(
                        error,
                        'Failed to update client contacts. Please try again.'
                    ),
                })
            },
        })
    }

    return (
        <DrawerComponent
            open={open}
            onClose={onClose}
            hasCancelButton
            onCancel={onClose}
            headerTitle="Client Contacts"
            primaryActionText="Save"
            onPrimaryAction={handleSave}
        >
            <StyledTabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
            >
                {Object.values(ContactGroups).map((group) => (
                    <Tab key={group} label={group} value={group} />
                ))}
            </StyledTabs>

            <ContentWrapper>
                {/* Primary Contact Autocomplete */}
                <Box>
                    <Typography variant="h5" sx={{ py: 1 }}>
                        Primary Contact
                    </Typography>
                    <Autocomplete
                        fullWidth
                        options={
                            data?.company.employees?.filter(
                                (employee) =>
                                    employee.id !==
                                    secondaryContact?.candidateId
                            ) ?? []
                        }
                        getOptionLabel={(option) =>
                            `${option.user?.nickname ?? option.user?.firstName} ${option.user?.lastName}`
                        }
                        value={
                            data?.company.employees?.find(
                                (employee) =>
                                    employee.id ===
                                    updatedContacts.get(
                                        `${selectedTab}-Primary Contact`
                                    )?.candidateId
                            ) ?? null
                        }
                        onChange={(event, newValue) =>
                            handlePrimaryContactChange(newValue)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Primary Recruiter Name"
                                variant="outlined"
                            />
                        )}
                    />
                </Box>

                {/* Secondary Contact Autocomplete */}
                <Box>
                    <Typography variant="h5" sx={{ py: 1 }}>
                        Secondary Contact
                    </Typography>
                    <Autocomplete
                        fullWidth
                        options={
                            data?.company.employees?.filter(
                                (employee) =>
                                    employee.id !== primaryContact?.candidateId
                            ) ?? []
                        }
                        getOptionLabel={(option) =>
                            `${option.user?.nickname ?? option.user?.firstName} ${option.user?.lastName}`
                        }
                        value={
                            data?.company.employees?.find(
                                (employee) =>
                                    employee.id ===
                                    updatedContacts.get(
                                        `${selectedTab}-Secondary Contact`
                                    )?.candidateId
                            ) ?? null
                        }
                        onChange={(event, newValue) =>
                            handleSecondaryContactChange(newValue)
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Secondary Recruiter Name"
                                variant="outlined"
                            />
                        )}
                        sx={{ marginBottom: getPxAsRem(20) }}
                    />
                </Box>
            </ContentWrapper>
        </DrawerComponent>
    )
}

export default ClientContactsDrawer
